import { useEffect, useMemo } from 'react';
import {
  OASISRadio,
  OASISYesNoCheckbox,
  OASISSelect,
  OASISText,
  OASISDate,
  OASISPhysician,
  OASISTime,
  OASISNumber,
  OASISMultiselect,
  OASISMultiselectOption,
  OASISRadioOption,
  useBespokeSectionContext,
} from '../primitives';
import { layoutInlineStyle } from './layoutInlineStyle';
import {
  holdsOnlyUnservedFields,
  reportHiddenFields,
  unservedFieldQids,
} from './reportHiddenFields';
import { isContainerNodeType, isFieldNodeType } from './layoutNodeKinds';
import type { LayoutNodeRenderContext } from './nodeRenderProps';
import { Box } from './nodes/Box';
import { Card } from './nodes/Card';
import { Conditional } from './nodes/Conditional';
import { DisabledWhenBlock } from './nodes/DisabledWhenBlock';
import { DisplayBlock } from './nodes/DisplayBlock';
import { ExternalAnswer } from './nodes/ExternalAnswer';
import { Divider } from './nodes/Divider';
import { FieldRow } from './nodes/FieldRow';
import { Flex } from './nodes/Flex';
import { Grid } from './nodes/Grid';
import { Indent } from './nodes/Indent';
import { RepeatBlock } from './nodes/RepeatBlock';
import { SectionLabel } from './nodes/SectionLabel';
import { Stack } from './nodes/Stack';
import { StaticTextBlock } from './nodes/StaticTextBlock';
import { Table } from './nodes/Table';
import type { LayoutNode } from './types';

/**
 * True when `node` should render nothing because the questions it exists to
 * hold are not served anywhere on the form (print judges the whole form too):
 * a field whose own qid is missing, or a container (tables included) whose
 * field qids are all missing (a container with no fields, such as static text
 * alone, is kept). The container walk counts both `conditional` branches, as
 * `collectLayoutRenderableFieldQids` does, so a group is only dropped when no
 * branch could ever show a served field. `repeat`, `conditional` and
 * `disabled-when` are never dropped whole; the fields inside them still are.
 */
function holdsOnlyFieldsMissingFromSchema(
  node: LayoutNode,
  schemaMap: ReadonlyMap<string, unknown>
): boolean {
  if (isFieldNodeType(node.type)) {
    return !schemaMap.has((node as { qid: string }).qid);
  }
  if (isContainerNodeType(node.type)) {
    return holdsOnlyUnservedFields([node], schemaMap);
  }
  return false;
}

export function RenderNode(
  props: LayoutNodeRenderContext & { node: LayoutNode }
) {
  const { schemaMap, hideFieldsMissingFromSchema } = useBespokeSectionContext();
  const hidden = useMemo(
    () =>
      hideFieldsMissingFromSchema &&
      holdsOnlyFieldsMissingFromSchema(props.node, schemaMap),
    [hideFieldsMissingFromSchema, props.node, schemaMap]
  );
  useEffect(() => {
    if (hidden) reportHiddenFields(unservedFieldQids([props.node], schemaMap));
  }, [hidden, props.node, schemaMap]);
  if (hidden) return null;
  return renderLayoutNode(props);
}

function renderLayoutNode({
  node,
  answers,
  repeatBindings,
}: LayoutNodeRenderContext & { node: LayoutNode }) {
  switch (node.type) {
    case 'card':
      return (
        <Card node={node} answers={answers} repeatBindings={repeatBindings} />
      );
    case 'stack':
      return (
        <Stack node={node} answers={answers} repeatBindings={repeatBindings} />
      );
    case 'grid':
      return (
        <Grid node={node} answers={answers} repeatBindings={repeatBindings} />
      );
    case 'flex':
      return (
        <Flex node={node} answers={answers} repeatBindings={repeatBindings} />
      );
    case 'box':
      return (
        <Box node={node} answers={answers} repeatBindings={repeatBindings} />
      );
    case 'indent':
      return (
        <Indent node={node} answers={answers} repeatBindings={repeatBindings} />
      );
    case 'divider':
      return <Divider node={node} />;
    case 'static':
      return (
        <StaticTextBlock
          text={node.text}
          as={node.as ?? 'div'}
          variant={node.variant}
          className={node.className}
          style={node.style}
        />
      );
    case 'text':
      return (
        <StaticTextBlock
          text={node.text}
          as={node.as ?? 'span'}
          variant={node.variant}
          className={node.className}
          style={node.style}
        />
      );
    case 'field-row':
      return (
        <FieldRow
          node={node}
          answers={answers}
          repeatBindings={repeatBindings}
        />
      );
    case 'sum':
      return null;
    case 'display':
      return <DisplayBlock node={node} />;
    case 'external-answer':
      return <ExternalAnswer node={node} />;
    case 'section-label':
      return <SectionLabel node={node} />;
    case 'table':
      return (
        <Table
          node={node}
          answers={answers}
          repeatBindings={repeatBindings}
          renderNode={renderNodeElement}
        />
      );
    case 'conditional':
      return (
        <Conditional
          node={node}
          answers={answers}
          repeatBindings={repeatBindings}
        />
      );
    case 'disabled-when':
      return (
        <DisabledWhenBlock
          node={node}
          answers={answers}
          repeatBindings={repeatBindings}
        />
      );
    case 'repeat':
      return (
        <RepeatBlock
          node={node}
          answers={answers}
          repeatBindings={repeatBindings}
        />
      );

    case 'OASISRadio':
      return (
        <OASISRadio
          qid={node.qid}
          labelOverride={node.labelOverride}
          hideLabel={node.hideLabel}
          inline={node.inline}
          className={node.className}
          style={layoutInlineStyle(node.style)}
          innerClassName={node.innerClassName}
          labelClassName={node.labelClassName}
          groupClassName={node.groupClassName}
          optionRowClassName={node.optionRowClassName}
          optionLabelClassName={node.optionLabelClassName}
          controlClassName={node.controlClassName}
          itemClassName={node.itemClassName}
          orientation={node.orientation}
        />
      );
    case 'OASISYesNoCheckbox':
      return (
        <OASISYesNoCheckbox
          qid={node.qid}
          labelOverride={node.labelOverride}
          hideLabel={node.hideLabel}
          className={node.className}
          style={layoutInlineStyle(node.style)}
          innerClassName={node.innerClassName}
          labelClassName={node.labelClassName}
          optionRowClassName={node.optionRowClassName}
          controlClassName={node.controlClassName}
          checkboxClassName={node.checkboxClassName}
        />
      );
    case 'OASISSelect':
      return (
        <OASISSelect
          qid={node.qid}
          labelOverride={node.labelOverride}
          hideLabel={node.hideLabel}
          className={node.className}
          style={layoutInlineStyle(node.style)}
          innerClassName={node.innerClassName}
          labelClassName={node.labelClassName}
          controlClassName={node.controlClassName}
          triggerClassName={node.triggerClassName}
          placeholder={node.placeholder}
        />
      );
    case 'OASISText':
      return (
        <OASISText
          qid={node.qid}
          labelOverride={node.labelOverride}
          multiline={node.multiline}
          hideLabel={node.hideLabel}
          className={node.className}
          style={layoutInlineStyle(node.style)}
          innerClassName={node.innerClassName}
          labelClassName={node.labelClassName}
          controlClassName={node.controlClassName}
          inputClassName={node.inputClassName}
        />
      );
    case 'OASISPhysician':
      return (
        <OASISPhysician
          qid={node.qid}
          labelOverride={node.labelOverride}
          hideLabel={node.hideLabel}
          className={node.className}
          style={layoutInlineStyle(node.style)}
          innerClassName={node.innerClassName}
          labelClassName={node.labelClassName}
          headerClassName={node.headerClassName}
          actionsClassName={node.actionsClassName}
          controlClassName={node.controlClassName}
          optionRowClassName={node.optionRowClassName}
          optionLabelClassName={node.optionLabelClassName}
          detailsClassName={node.detailsClassName}
          inputClassName={node.inputClassName}
        />
      );
    case 'OASISDate':
      return (
        <OASISDate
          qid={node.qid}
          labelOverride={node.labelOverride}
          hideLabel={node.hideLabel}
          className={node.className}
          style={layoutInlineStyle(node.style)}
          innerClassName={node.innerClassName}
          labelClassName={node.labelClassName}
          controlClassName={node.controlClassName}
          inputClassName={node.inputClassName}
        />
      );
    case 'OASISTime':
      return (
        <OASISTime
          qid={node.qid}
          labelOverride={node.labelOverride}
          hideLabel={node.hideLabel}
          className={node.className}
          style={layoutInlineStyle(node.style)}
          innerClassName={node.innerClassName}
          labelClassName={node.labelClassName}
          controlClassName={node.controlClassName}
          inputClassName={node.inputClassName}
        />
      );
    case 'OASISNumber':
      return (
        <OASISNumber
          qid={node.qid}
          labelOverride={node.labelOverride}
          hideLabel={node.hideLabel}
          className={node.className}
          style={layoutInlineStyle(node.style)}
          innerClassName={node.innerClassName}
          labelClassName={node.labelClassName}
          controlClassName={node.controlClassName}
          inputClassName={node.inputClassName}
        />
      );
    case 'OASISMultiselect': {
      const hasCompound = node.children != null && node.children.length > 0;
      return (
        <OASISMultiselect
          qid={node.qid}
          labelOverride={node.labelOverride}
          hideLabel={node.hideLabel}
          className={node.className}
          style={layoutInlineStyle(node.style)}
          innerClassName={node.innerClassName}
          labelClassName={node.labelClassName}
          checklistClassName={node.checklistClassName}
          checklistStyle={layoutInlineStyle(node.checklistStyle)}
          optionRowClassName={node.optionRowClassName}
          optionLabelClassName={node.optionLabelClassName}
          controlClassName={node.controlClassName}
          checkboxClassName={node.checkboxClassName}
          compoundContent={
            hasCompound ? (
              <RenderNodes
                nodes={node.children!}
                answers={answers}
                repeatBindings={repeatBindings}
              />
            ) : undefined
          }
        />
      );
    }
    case 'OASISMultiselectOption':
      return (
        <OASISMultiselectOption
          value={node.value}
          valueFrom={node.valueFrom}
          labelOverride={node.labelOverride}
          className={node.className}
          style={layoutInlineStyle(node.style)}
          labelClassName={node.labelClassName}
          optionRowClassName={node.optionRowClassName}
          controlClassName={node.controlClassName}
          checkboxClassName={node.checkboxClassName}
        />
      );
    case 'radio-option':
      return (
        <OASISRadioOption
          qid={node.qid}
          value={node.value}
          className={node.className}
          style={layoutInlineStyle(node.style)}
          controlClassName={node.controlClassName}
          itemClassName={node.itemClassName}
        />
      );
  }
}

// `Table` calls `renderNode` as a function, so hand it an element: calling
// `RenderNode` directly would run its hooks inside `Table`'s render.
function renderNodeElement(
  props: LayoutNodeRenderContext & { node: LayoutNode }
) {
  return <RenderNode {...props} />;
}

export function RenderNodes({
  nodes,
  answers,
  repeatBindings,
}: LayoutNodeRenderContext & { nodes: LayoutNode[] }) {
  return (
    <>
      {nodes.map((node, i) => (
        <RenderNode
          key={i}
          node={node}
          answers={answers}
          repeatBindings={repeatBindings}
        />
      ))}
    </>
  );
}
